<template>
  <div id="modifyProduct">
    <Loading v-if="loading"></Loading>
    <van-form @submit="onSubmit">
      <!-- 商品类型 -->
      <van-field
        autocomplete="off"
        :value="productTypeText"
        name="productType"
        label="商品类型"
        disabled
      />

      <!-- 基本信息 -->
      <van-cell-group inset>
        <div class="section-title">基本信息</div>
        <van-field
          autocomplete="off"
          v-model="form.code"
          name="code"
          label="编号"
          disabled
        />

        <van-field
          autocomplete="off"
          v-model="form.name"
          name="name"
          label="商品名称"
          required
        />

        <van-field
          autocomplete="off"
          v-model="form.shortName"
          name="shortName"
          label="商品简称"
        />

        <van-cell
          title="图片"
          autocomplete="off"
          inputmode="none"
          v-model="form.picture"
        >
          <UploadImage v-model="form.picture" @change="imageChange"
        /></van-cell>

        <van-field
          autocomplete="off"
          label="外部编号"
          name="externalCode"
          v-model:value="form.externalCode"
          :class="{ 'external-code-highlight': externalCodeHighlight }"
        />

        <van-cell @click="showDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="form.categoryName"
            name="categoryName"
            label="商品类目"
            required
          >
          </van-field>
        </van-cell>
        <van-cell @click="showBrandDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="form.brandName"
            name="brandName"
            label="商品品牌"
            required
          >
          </van-field>
        </van-cell>
        <van-cell @click="showPlatformCategoryDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="form.supCategoryName"
            name="supCategoryName"
            label="平台类目"
          >
          </van-field>
        </van-cell>

        <van-field
          autocomplete="off"
          label="规格"
          name="spec"
          v-model:value="form.spec"
          allow-clear
        >
        </van-field>

        <van-cell @click="showSupplierSelect" v-if="form.productType !== 2">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="form.supplierName"
            name="supplierName"
            label="供应商"
            required
          >
          </van-field>
        </van-cell>

        <van-field
          autocomplete="off"
          :value="businessModelText"
          name="businessModel"
          label="经营模式"
          readonly
          v-if="form.productType !== 2"
        />

        <van-field
          autocomplete="off"
          v-model="form.available"
          name="available"
          label="状态"
          required
        >
          <template #input>
            <van-dropdown-menu style="width: 100%">
              <van-dropdown-item
                v-model="form.available"
                :options="statusList"
              />
            </van-dropdown-menu>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 属性价格 -->
      <van-cell-group inset>
        <div class="section-title">属性价格</div>
        <van-field
          autocomplete="off"
          name="isWeight"
          label="称重商品"
          required
          disabled
        >
          <template #input>
            <van-radio-group
              v-model="form.isWeight"
              direction="horizontal"
              disabled
            >
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          label="单位"
          name="unit"
          v-model:value="form.unit"
          allow-clear
          required
          @click="unitSelectVisible = true"
        />

        <UnitSelect :visible="unitSelectVisible" @confirm="unitSelectConfirm" />

        <van-field
          autocomplete="off"
          v-model="form.weight"
          name="weight"
          type="number"
          label="固定重量"
        >
          <template #right-icon>
            <span>kg</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.volume"
          name="volume"
          type="number"
          label="体积"
        >
          <template #right-icon>
            <span>cm³</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.retailPrice"
          required
          type="number"
          name="retailPrice"
          label="零售价"
          @change="handleRetailPriceChange"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.purchasePrice"
          :disabled="form.productType == 2"
          name="purchasePrice"
          type="number"
          label="采购价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 其他价格 -->
      <van-cell-group inset>
        <div class="section-title">其他价格</div>
        <van-field
          autocomplete="off"
          v-model="form.memberPrice"
          name="memberPrice"
          type="number"
          label="会员价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          v-if="form.productType !== 2"
          autocomplete="off"
          v-model="form.salePrice"
          name="salePrice"
          type="number"
          label="批发价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.taxRate"
          name="taxRate"
          type="number"
          label="进项税率"
          v-if="form.productType !== 2"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.saleTaxRate"
          type="number"
          name="saleTaxRate"
          label="销项税率"
          v-if="form.productType !== 2"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="form.profitPercent"
          name="profitPercent"
          type="number"
          label="分销比例"
          v-if="form.productType !== 2"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 扩展信息 -->
      <van-cell-group inset v-if="form.productType !== 2">
        <div class="section-title">扩展信息</div>
        <van-field autocomplete="off" name="scoreFlag" label="积分赠送">
          <template #input>
            <van-radio-group v-model="form.scoreFlag" direction="horizontal">
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field autocomplete="off" name="machFlag" label="无人柜商品">
          <template #input>
            <van-radio-group v-model="form.machFlag" direction="horizontal">
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 组合商品明细区域 -->
      <div v-if="form.productType === 2">
        <div class="section-title">组合商品明细</div>
        <div
          style="padding: 10px 0; display: flex; justify-content: space-between"
        >
          <van-button
            type="primary"
            size="small"
            @click="selectProduct"
            native-type="button"
            >新增商品</van-button
          >
          <van-button
            type="primary"
            size="small"
            @click="calculateApportionment"
            :disabled="!canCalculateApportionment"
            native-type="button"
            >自动计算分摊比率</van-button
          >
        </div>

        <!-- 商品明细表格 -->
        <ve-table
          class="table"
          :columns="productBundleColumns"
          :table-data="productBundles"
          :border-around="true"
          :border-x="true"
          :border-y="true"
          :row-key-field-name="'productId'"
          :expand-option="expandOption"
          :checkbox-option="checkboxOption"
          :cellSelectionOption="cellSelectionOption"
        ></ve-table>
      </div>

      <div style="margin: 16px 0">
        <van-button
          round
          block
          type="primary"
          :loading="loading"
          native-type="submit"
        >
          提交
        </van-button>
      </div>
    </van-form>

    <CategorySelect
      :visible="categorySelectVisible"
      @confirm="categorySelectConfirm"
    ></CategorySelect>

    <CategorySelect
      :visible="platformCategorySelectVisible"
      :requestParams="{ platformFlag: 1 }"
      @confirm="platformCategorySelectConfirm"
    ></CategorySelect>

    <SupplierSelect
      :visible="supplierSelectVisible"
      @confirm="supplierSelectConfirm"
    ></SupplierSelect>

    <ProductSelect
      :requestParams="{ productType: 1 }"
      columnsType="checkbox"
      :visible="productSelectVisible"
      @confirm="productSelectConfirm"
    ></ProductSelect>
    <BrandSelect
      :visible="brandSelectVisible"
      @confirm="brandSelectConfirm"
    ></BrandSelect>
  </div>
</template>
<script>
import { get, update } from "../../service/api/product";
import { getCamelCharsUpperCase, cleanedStr } from "@/utils/index";
import CategorySelect from "../../components/categorySelect.vue";
import { Toast, Dialog } from "vant";
import UploadImage from "../../components/uploadImage.vue";
import * as api from "../../service/api/index";
import Loading from "../../components/loading.vue";
import UnitSelect from "../../components/unitSelect.vue";
import SupplierSelect from "../../components/supplierSelect.vue";
import ProductSelect from "../../components/productSelect.vue";
import BrandSelect from "../../components/brandSelect.vue";

export default {
  name: "index",
  components: {
    CategorySelect,
    UploadImage,
    Loading,
    SupplierSelect,
    ProductSelect,
    BrandSelect,
    UnitSelect,
  },
  data() {
    return {
      cellSelectionOption: {
        // disble cell selection
        enable: false,
      },
      expandOption: {
        trigger: "row",
        render: ({ row, column, rowIndex }, h) => {
          var expandColumns = [
            {
              field: "bundleNum",
              key: "bundleNum",
              title: "包含数量",
              width: 120,
              align: "center",
              renderBodyCell: ({ row, column, rowIndex }, h) => {
                return (
                  <input
                    style="width:100%;height:1.5rem;border: 1px solid rgb(220, 223, 230); border-radius: 4px; "
                    value={row[column.field]}
                    inputmode="numeric"
                    on-input={(e) => this.handleBundleNumChange(e, row)}
                  ></input>
                );
              },
            },
            {
              field: "oriRetailPrice",
              key: "oriRetailPrice",
              title: "原零售价",
              width: 120,
              align: "center",
            },
            {
              field: "bundlePercent",
              key: "bundlePercent",
              title: "分摊占比（%）",
              width: 120,
              align: "center",
              renderBodyCell: ({ row, column, rowIndex }, h) => {
                return (
                  <input
                    style="width:100%;height:1.5rem;border: 1px solid rgb(220, 223, 230); border-radius: 4px; "
                    inputmode="numeric"
                    value={row[column.field]}
                    on-input={(e) =>
                      this.handleApportionmentRatioChange(e, row)
                    }
                  ></input>
                );
              },
            },
            {
              field: "retailPrice",
              key: "retailPrice",
              title: "分摊零售价（元）",
              width: 120,
              align: "center",
            },
          ];
          var expandTableData = [row];
          return h(
            "div",
            {
              style: {
                border: "1px solid #eee",
                backgroundColor: "#fafafa",
              },
            },
            [
              h("ve-table", {
                props: {
                  columns: expandColumns,
                  tableData: expandTableData,
                  borderAround: true,
                  borderX: true,
                  borderY: true,
                  rowKeyFieldName: "attribute",
                },
                style: {
                  width: "100%",
                  border: "1px solid #dcdfe6",
                  borderRadius: "4px",
                },
              }),
            ]
          );
        },
        afterExpandRowChange: ({ afterExpandedRowKeys, row, rowIndex }) => {
          this.expandedRowKeys = afterExpandedRowKeys;
        },
      },
      checkboxOption: {
        // 行选择改变事件
        selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
          console.log(row, isSelected, selectedRowKeys);
        },
        // 全选改变事件
        selectedAllChange: ({ isSelected, selectedRowKeys }) => {
          console.log(isSelected, selectedRowKeys);
        },
      },

      loading: false,
      brandSelectVisible: false,
      categorySelectVisible: false,
      platformCategorySelectVisible: false,
      supplierSelectVisible: false,
      unitSelectVisible: false,
      productSelectVisible: false,
      currentProductIndex: -1,
      productBundles: [], // 组合商品明细列表
      expandedRowKeys: [], // 扩展行状态管理
      productBundleColumns: [], // 商品明细表格列配置
      id: this.$route.params.id,
      statusList: [
        { text: "启用", value: "1" },
        { text: "停用", value: "0" },
      ],
      businessModelList: [
        { text: "经销", value: "1" },
        { text: "联营", value: "2" },
      ],
      productTypeList: [
        { text: "普通商品", value: 1 },
        { text: "组合商品", value: 2 },
      ],
      formData: null,
      form: {
        id: "",
        name: "",
        retailPrice: "",
        categoryName: "",
        unit: "",
        externalCode: "",
        supplierId: "",
        supplierName: "",
        businessModel: "",
        productType: "",
        supCategoryName: "",
        supCategoryId: "",
        isWeight: 0,
        weight: "",
        volume: "",
        memberPrice: "",
        salePrice: "",
        scoreFlag: 1,
        machFlag: 0,
        profitPercent: "",
      },
      priceUnit: "元/件",
      scanBuffer: "", // 扫码缓冲区
      externalCodeHighlight: false,
    };
  },
  computed: {
    businessModelText() {
      if (this.form.businessModel == "1") return "经销";
      if (this.form.businessModel == "2") return "联营";
      return this.form.businessModel;
    },
    productTypeText() {
      if (this.form.productType == "1") return "普通商品";
      if (this.form.productType == "2") return "组合商品";
      return this.form.productType;
    },
    canCalculateApportionment() {
      if (this.form.productType != 2) {
        return false;
      }
      // 检查是否有商品，且每个商品都有原零售价和数量
      if (!this.productBundles || this.productBundles.length === 0) {
        return false;
      }

      // 检查所有商品是否包含必要字段且数值有效
      for (const item of this.productBundles) {
        if (
          this.$utils.isEmpty(item.productId) ||
          this.$utils.isEmpty(item.bundleNum) ||
          this.$utils.isEmpty(item.oriRetailPrice) ||
          isNaN(parseFloat(item.bundleNum)) ||
          parseFloat(item.bundleNum) <= 0 ||
          isNaN(parseFloat(item.oriRetailPrice)) ||
          parseFloat(item.oriRetailPrice) <= 0
        ) {
          return false;
        }
      }

      // 检查零售价是否有效
      if (
        this.$utils.isEmpty(this.form.retailPrice) ||
        isNaN(parseFloat(this.form.retailPrice)) ||
        parseFloat(this.form.retailPrice) <= 0
      ) {
        return false;
      }

      return true;
    },
  },
  watch: {
    productBundles: {
      handler(newVal, oldVal) {
        this.calculateBundlePurchasePrice();
      },
      deep: true,
    },
    "form.name": {
      handler(newVal, oldVal) {
        this.form.shortName = cleanedStr(getCamelCharsUpperCase(newVal));
      },
    },
    "form.unit": {
      handler(newVal) {
        this.priceUnit = newVal ? `元/${newVal}` : "元/件";
      },
      immediate: true,
    },
  },
  created() {
    // 初始化商品明细表格列配置
    this.productBundleColumns = [
      {
        type: "expand",
        key: "expand",
        width: 40,
        align: "center",
      },
      {
        field: "productName",
        key: "productName",
        title: "单品",
        width: "auto",
        align: "left",
      },
      {
        field: "action",
        key: "action",
        title: "操作",
        width: "auto",
        align: "center",
        renderBodyCell: ({ rowIndex }) => {
          return (
            <span>
              <button
                class="del-btn"
                on-click={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  event.target.blur(); // 移除按钮的焦点
                  document.activeElement.blur(); // 移除页面上所有元素的焦点
                  this.removeProductItem(rowIndex);
                }}
                nativeType="button"
              >
                删除
              </button>
            </span>
          );
        },
      },
    ];
  },
  async mounted() {
    this.loading = true;
    const res = await get(this.id);
    this.loading = false;
    this.form = {
      ...res.data,
      available: res.data.available ? "1" : "0",
      scoreFlag: res.data.scoreFlag !== undefined ? res.data.scoreFlag : 1,
      machFlag: res.data.machFlag !== undefined ? res.data.machFlag : 0,
    };

    // 处理组合商品明细数据
    if (this.form.productType === 2 && res.data.productBundles) {
      this.productBundles = res.data.productBundles;
    }
    
    // 添加键盘事件监听
    document.addEventListener('keydown', this.handleExternalCodeInput);
  },
  beforeDestroy() {
    // 移除键盘事件监听
    document.removeEventListener('keydown', this.handleExternalCodeInput);
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.previousRoute = from;
    });
  },
  methods: {
    calculateBundlePurchasePrice() {
      if (this.form.productType === 2) {
        let totalPurchasePrice = 0;
        this.productBundles.forEach((item) => {
          if (
            !this.$utils.isEmpty(item.purchasePrice) &&
            !this.$utils.isEmpty(item.bundleNum)
          ) {
            const price = parseFloat(item.purchasePrice) || 0;
            const num = parseFloat(item.bundleNum) || 0;
            // 采购价 = 单价 × 数量
            const itemTotalPrice = this.$utils.mul(price, num);
            totalPurchasePrice = this.$utils.preciseAdd(
              totalPurchasePrice,
              itemTotalPrice
            );
          }
        });

        const finalPrice = this.$utils.toFixed(totalPurchasePrice, 2);
        console.log("组合商品采购价计算:", finalPrice);
        this.form.purchasePrice = finalPrice;
      }
    },
    roundToTwo(num, decimalPlaces = 2) {
      // 将数字四舍五入到指定小数位数
      return this.$utils.roundToTwo(num, {
        decimalPlaces,
        roundingMode: "round",
      });
    },
    imageChange(formData) {
      this.formData = formData;
    },
    async images_upload_handler() {
      const res = await api.uploadImage(this.formData);
      this.form.picture = res.data;
    },
    categorySelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.categorySelectVisible = false;
        return;
      }
      this.categorySelectVisible = false;
      this.form.categoryName = row.name;
      this.form.categoryId = row.id;
    },
    showBrandDialog() {
      this.brandSelectVisible = true;
    },
    showDialog() {
      this.categorySelectVisible = true;
    },
    showPlatformCategoryDialog() {
      this.platformCategorySelectVisible = true;
    },
    showSupplierSelect() {
      this.supplierSelectVisible = true;
    },
    supplierSelectConfirm(row) {
      if (Object.keys(row).length === 0) {
        this.supplierSelectVisible = false;
        return;
      }
      this.supplierSelectVisible = false;
      this.form.supplierName = row.name;
      this.form.supplierId = row.id;
      const code = row.code;
      if (code) {
        this.form.businessModel = row.manageType;
      } else {
        this.form.businessModel = undefined;
      }
    },
    unitSelectConfirm(item) {
      this.form.unit = item.name;
      this.form.unitCode = item.code;
      this.unitSelectVisible = false;
    },
    platformCategorySelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.platformCategorySelectVisible = false;
        return;
      }
      this.platformCategorySelectVisible = false;
      this.form.supCategoryName = row.name;
      this.form.supCategoryId = row.id;
    },
    // 组合商品明细相关方法
    // 添加组合商品明细项
    addProductItem() {
      this.productBundles.push({
        productId: "",
        product: "",
        bundleNum: null,
        oriRetailPrice: null,
        bundlePercent: null,
        retailPrice: null,
      });
    },
    removeProductItem(index) {
      if (typeof index === "number") {
        // 删除指定索引的商品项
        if (index >= 0 && index < this.productBundles.length) {
          this.productBundles.splice(index, 1);
          this.updateAllCalculations();
        }
      } else {
        // 默认行为：删除最后一个商品项
        if (this.productBundles.length > 0) {
          this.productBundles.pop();
          this.updateAllCalculations();
        }
      }
    },
    handleRowClick({ row }) {
      // 移除页面上所有元素的焦点，防止键盘弹出
      document.activeElement.blur();

      const rowKey = row.productId || row.index;
      const index = this.expandedRowKeys.indexOf(rowKey);
      if (index > -1) {
        this.expandedRowKeys.splice(index, 1);
      } else {
        this.expandedRowKeys.push(rowKey);
      }
    },
    brandSelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.brandSelectVisible = false;
        return;
      }
      this.brandSelectVisible = false;
      this.form.brandName = row.name;
      this.form.brandId = row.id;
    },
    selectProduct(index) {
      this.currentProductIndex = index;
      this.productSelectVisible = true;
    },
    productSelectConfirm(list) {
      console.log(list, "list");
      if (!list.length) {
        this.productSelectVisible = false;
        return;
      }

      this.productSelectVisible = false;
      list.forEach((item) => {
        // 检查商品是否已存在于productBundles中
        const existingIndex = this.productBundles.findIndex(
          (bundle) => bundle.productId === item.id
        );

        if (existingIndex >= 0) {
          // 商品已存在，只增加数量
          this.productBundles[existingIndex].bundleNum =
            Number(this.productBundles[existingIndex].bundleNum) + 1;
          this.handleBundleNumChange(null, this.productBundles[existingIndex]);
        } else {
          // 商品不存在，添加新行
          const row = {
            productName: item.name,
            productId: item.id,
            bundleNum: 1,
            oriRetailPrice: item.retailPrice,
            salePrice: 0,
          };
          this.productBundles.push(row);
        }
      });
    },
    // 处理扫码输入
    handleExternalCodeInput(event) {
      if (event.key.length === 1) {
        if (!/^\d$/.test(event.key)) {
          return;
        }
        this.scanBuffer += event.key;
        event.preventDefault();
      } else if (event.key === "Enter") {
        // 处理回车键
        if (this.scanBuffer) {
          if (this.scanBuffer.length >= 6 && this.scanBuffer.length <= 15) {
            this.form.externalCode = this.scanBuffer;
            this.$set(this.form, "externalCode", this.scanBuffer);
            // 添加高亮效果
            this.externalCodeHighlight = true;
            setTimeout(() => {
              this.externalCodeHighlight = false;
            }, 2000); // 2秒后恢复正常
          } else {
            Toast.fail("条形码有误，请重新扫描:" + this.scanBuffer);
          }
          this.scanBuffer = "";
        }
        event.preventDefault();
      }
    },
    validData() {
      if (this.$utils.isEmpty(this.form.name)) {
        this.$utils.createError("商品名称不允许为空！");
        return false;
      }
      if (this.$utils.isEmpty(this.form.categoryId)) {
        this.$utils.createError("商品类目不允许为空！");
        return false;
      }

      // 只有非组合商品需要验证供应商
      if (
        this.form.productType !== 2 &&
        this.$utils.isEmpty(this.form.supplierId)
      ) {
        this.$utils.createError("供应商不允许为空！");
        return false;
      }

      if (this.$utils.isEmpty(this.form.unit)) {
        this.$utils.createError("商品单位不允许为空！");
        return false;
      }
      if (!this.$utils.isEmpty(this.form.weight)) {
        if (!this.$utils.isFloat(this.form.weight)) {
          this.$utils.createError("重量（kg）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.weight)) {
          this.$utils.createError("重量（kg）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.weight, 2)) {
          this.$utils.createError("重量（kg）最多允许2位小数");
          return false;
        }
      }
      if (!this.$utils.isEmpty(this.form.volume)) {
        if (!this.$utils.isFloat(this.form.volume)) {
          this.$utils.createError("体积（cm³）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.volume)) {
          this.$utils.createError("体积（cm³）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.volume, 2)) {
          this.$utils.createError("体积（cm³）最多允许2位小数");
          return false;
        }
      }
      if (!this.$utils.isEmpty(this.form.taxRate)) {
        if (!this.$utils.isInteger(this.form.taxRate)) {
          this.$utils.createError("进项税率（%）必须为整数");
          return false;
        }
        if (!this.$utils.isIntegerGeZero(this.form.taxRate)) {
          this.$utils.createError("进项税率（%）不允许小于0");
          return false;
        }
      }
      if (!this.$utils.isEmpty(this.form.saleTaxRate)) {
        if (!this.$utils.isInteger(this.form.saleTaxRate)) {
          this.$utils.createError("销项税率（%）必须为整数");
          return false;
        }
        if (!this.$utils.isIntegerGeZero(this.form.saleTaxRate)) {
          this.$utils.createError("销项税率（%）不允许小于0");
          return false;
        }
      }
      if (!this.$utils.isEmpty(this.form.purchasePrice)) {
        if (!this.$utils.isFloat(this.form.purchasePrice)) {
          this.$utils.createError("采购价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.purchasePrice)) {
          this.$utils.createError("采购价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.purchasePrice, 2)) {
          this.$utils.createError("采购价（元）最多允许2位小数");
          return false;
        }
      }
      // if (this.$utils.isEmpty(this.form.salePrice)) {
      //   this.$utils.createError("批发价（元）不允许为空！");
      //   return false;
      // }
      if (this.$utils.isEmpty(this.form.retailPrice)) {
        this.$utils.createError("零售价（元）不允许为空！");
        return false;
      }
      if (!this.$utils.isEmpty(this.form.retailPrice)) {
        if (!this.$utils.isFloat(this.form.retailPrice)) {
          this.$utils.createError("零售价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.retailPrice)) {
          this.$utils.createError("零售价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.retailPrice, 2)) {
          this.$utils.createError("零售价（元）最多允许2位小数");
          return false;
        }
      }
      // 会员价验证
      if (!this.$utils.isEmpty(this.form.memberPrice)) {
        if (!this.$utils.isFloat(this.form.memberPrice)) {
          this.$utils.createError("会员价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.memberPrice)) {
          this.$utils.createError("会员价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.memberPrice, 2)) {
          this.$utils.createError("会员价（元）最多允许2位小数");
          return false;
        }
      }
      // 批发价验证
      if (!this.$utils.isEmpty(this.form.salePrice)) {
        if (!this.$utils.isFloat(this.form.salePrice)) {
          this.$utils.createError("批发价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.form.salePrice)) {
          this.$utils.createError("批发价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.form.salePrice, 2)) {
          this.$utils.createError("批发价（元）最多允许2位小数");
          return false;
        }
      }

      // 组合商品明细验证
      if (this.form.productType === 2) {
        // 验证是否有商品明细
        if (!this.productBundles || this.productBundles.length === 0) {
          this.$utils.createError("组合商品必须添加至少一个商品明细！");
          return false;
        }

        // 验证每个商品明细的必填字段
        for (let i = 0; i < this.productBundles.length; i++) {
          const item = this.productBundles[i];

          // 验证商品ID是否存在
          if (!item.productId) {
            this.$utils.createError(`第${i + 1}个商品明细：请选择单品！`);
            return false;
          }

          // 验证包含数量是否存在且为有效数字
          if (!item.bundleNum || isNaN(item.bundleNum) || item.bundleNum <= 0) {
            this.$utils.createError(
              `第${i + 1}个商品明细：包含数量必须为大于0的数字！`
            );
            return false;
          }

          // 验证原零售价是否存在且为有效数字
          if (
            !item.oriRetailPrice ||
            isNaN(item.oriRetailPrice) ||
            item.oriRetailPrice <= 0
          ) {
            this.$utils.createError(
              `第${i + 1}个商品明细：原零售价必须为大于0的数字！`
            );
            return false;
          }

          // 验证分摊占比是否存在且为有效数字
          if (
            !item.bundlePercent ||
            isNaN(item.bundlePercent) ||
            item.bundlePercent <= 0
          ) {
            this.$utils.createError(
              `第${i + 1}个商品明细：分摊占比必须为大于0的数字！`
            );
            return false;
          }
        }

        // 验证分摊占比之和是否为100%
        let totalPercent = 0;
        this.productBundles.forEach((item) => {
          totalPercent += parseFloat(item.bundlePercent);
        });

        // 允许有0.01的误差
        if (Math.abs(totalPercent - 100) > 0.01) {
          this.$utils.createError(
            `分摊占比之和必须为100%，当前总和为${this.$utils.toFixed(
              totalPercent,
              2
            )}%！`
          );
          return false;
        }
      }
      return true;
    },
    async onSubmit() {
      try {
        if (!this.validData()) return;
        debugger;
        // 检查是否需要二次确认
        const retailPrice = parseFloat(this.form.retailPrice);
        const purchasePrice = parseFloat(this.form.purchasePrice);
        const memberPrice = parseFloat(this.form.memberPrice);
        const salePrice = parseFloat(this.form.salePrice);

        let needConfirm = false;
        let message = "";

        if (
          !isNaN(purchasePrice) &&
          !isNaN(retailPrice) &&
          purchasePrice > retailPrice
        ) {
          needConfirm = true;
          message += "采购价大于零售价，";
        }
        if (
          !isNaN(memberPrice) &&
          !isNaN(retailPrice) &&
          memberPrice > retailPrice
        ) {
          needConfirm = true;
          message += "会员价大于零售价，";
        }
        if (
          !isNaN(salePrice) &&
          !isNaN(retailPrice) &&
          salePrice > retailPrice
        ) {
          needConfirm = true;
          message += "批发价大于零售价，";
        }

        if (needConfirm) {
          // 去除最后一个逗号
          message = message.slice(0, -1);
          message += "  确定要保存吗？";

          try {
            await Dialog.confirm({
              title: "确认保存",
              message: message,
            });
          } catch (error) {
            // 用户取消保存
            return;
          }
        }
        this.loading = true;
        if (this.formData) await this.images_upload_handler();
        delete this.form.proChannel;
        const data = {
          ...this.form,
          available: this.form.available === "1" ? true : false,
          // 添加组合商品明细数据
          productBundles: this.productBundles.map((item) => ({
            ...item,
            salePrice: this.form.productType === 2 ? 0 : item.salePrice,
          })),
          salePrice: this.form.productType === 2 ? 0 : this.form.salePrice,
          images: this.formData?.images || [],
        };
        const res = await update(data);
        if (res.code === 200) {
          Toast.success("修改商品成功");
          this.$router.push({
            path: this.previousRoute.path,
            query: Object.assign(
              {},
              this.previousRoute.query,
              this.$route.query
            ),
          });
        }
        this.loading = false;
      } catch (error) {
        if (error.isCanceled) return;
        this.loading = false;
        console.error(error);
      }
    },
    // 处理零售价变化
    handleRetailPriceChange() {
      if (this.form.productType === 2) {
        // 清空所有商品明细的分摊占比和分摊零售价
        this.productBundles.forEach((item, index) => {
          this.$set(item, "bundlePercent", "");
          this.$set(item, "retailPrice", "");
        });
        // 强制更新整个productBundles数组以触发表格重新渲染
        this.productBundles = [...this.productBundles];
      }
    },
    // 处理包含数量变化
    handleBundleNumChange(e, row) {
      if (e && e.target) {
        const value = e.target.value;
        this.$set(row, "bundleNum", value);
      }
      this.$set(row, "bundlePercent", "");
      this.$set(row, "retailPrice", "");
      this.productBundles = [...this.productBundles];
    },
    // 处理分摊占比变化
    handleApportionmentRatioChange(e, row) {
      if (e) {
        const value = e.target.value;
        this.$set(row, "bundlePercent", value);
      }

      if (
        this.$utils.isEmpty(row.bundlePercent) ||
        this.$utils.isEmpty(this.form.retailPrice) ||
        this.$utils.isEmpty(row.bundleNum)
      ) {
        this.$set(row, "retailPrice", "");
        return;
      }
      // 计算分摊零售价：分摊零售价=分摊占比*零售价/数量
      // 分摊占比是百分比数字（如69.23），需要转换为小数
      const ratio = parseFloat(row.bundlePercent) || 0;
      const retailPrice = parseFloat(this.form.retailPrice) || 0;
      const bundleNum = parseFloat(row.bundleNum) || 0;

      if (bundleNum > 0) {
        // 使用精确计算函数避免浮点数精度问题
        const ratioDecimal = this.$utils.preciseDivision(ratio, 100); // 转换为小数
        const numerator = this.$utils.preciseMultiplication(
          ratioDecimal,
          retailPrice
        ); // 分子：占比*零售价
        const apportionedPrice = this.$utils.preciseDivision(
          numerator,
          bundleNum
        ); // 结果：分子/数量

        this.$set(row, "retailPrice", this.roundToTwo(apportionedPrice, 2));
      } else {
        this.$set(row, "retailPrice", "");
      }
      this.productBundles = [...this.productBundles];
    },
    // 根据售价自动计算分摊比率
    calculateApportionment() {
      debugger;
      if (!this.canCalculateApportionment) {
        this.$msg.createError("请先填写零售价和所有商品明细的数量及原零售价！");
        return;
      }
      const retailPrice = parseFloat(this.form.retailPrice) || 0;
      if (retailPrice <= 0) {
        this.$msg.createError("零售价必须大于0！");
        return;
      }
      // 计算原零售总价
      let totalOriginalRetailPrice = 0;
      this.productBundles.forEach((item) => {
        if (
          !this.$utils.isEmpty(item.bundleNum) &&
          !this.$utils.isEmpty(item.oriRetailPrice)
        ) {
          const num = parseFloat(item.bundleNum) || 0;
          const price = parseFloat(item.oriRetailPrice) || 0;
          totalOriginalRetailPrice = this.$utils.preciseAdd(
            totalOriginalRetailPrice,
            this.$utils.preciseMultiplication(num, price)
          );
        }
      });
      if (totalOriginalRetailPrice <= 0) {
        this.$msg.createError("原零售总价必须大于0！");
        return;
      }
      // 计算分摊占比：分摊占比=(数量*原零售价)/原零售总价
      let totalRatio = 0;
      const ratios = [];
      debugger;
      for (let i = 0; i < this.productBundles.length; i++) {
        const item = this.productBundles[i];
        if (
          !this.$utils.isEmpty(item.bundleNum) &&
          !this.$utils.isEmpty(item.oriRetailPrice)
        ) {
          const num = parseFloat(item.bundleNum) || 0;
          const price = parseFloat(item.oriRetailPrice) || 0;

          // 计算当前项的占比
          const currentItemTotal = this.$utils.preciseMultiplication(
            num,
            price
          );
          const ratio = this.roundToTwo(
            this.$utils.preciseDivision(
              currentItemTotal,
              totalOriginalRetailPrice
            ) * 100
          );

          ratios.push(ratio);

          // 累加前n-1项的占比，最后一项单独计算以确保总和为1
          if (i < this.productBundles.length - 1) {
            totalRatio = this.$utils.preciseAdd(totalRatio, ratio);
          }
        } else {
          ratios.push(0);
        }
      }

      // 最后一项使用减法：1 - 前n-1项之和，确保总占比为1（100%）
      if (this.productBundles.length > 0) {
        const lastIndex = this.productBundles.length - 1;
        ratios[lastIndex] = this.$utils.preciseSubtraction(100, totalRatio);
      }
      // 设置分摊占比并计算分摊零售价（存储为百分比数字，不包含%符号）
      this.productBundles.forEach((item, index) => {
        this.$set(item, "bundlePercent", ratios[index]);
        this.handleApportionmentRatioChange(null, item);
      });
      // 强制更新整个productBundles数组以触发表格重新渲染
      this.productBundles = [...this.productBundles];
      this.$forceUpdate();
    },
    // 更新所有商品的计算
    updateAllCalculations() {
      this.productBundles.forEach((item) => {
        this.handleApportionmentRatioChange(null, item);
      });
    },
  },
};
</script>

<style scoped>
::v-deep .external-code-highlight {
  animation: highlight 2s ease-in-out;
  background-color: rgba(0, 191, 165, 0.1);
}

@keyframes highlight {
  0% {   
    background-color: rgba(0, 191, 165, 0.1);
  }
  50% {
    background-color: rgba(0, 191, 165, 0.3);
  }
  100% {
    background-color: rgba(0, 191, 165, 0.1);
  }
}
</style>
<style scoped lang="less">
#modifyProduct {
  height: 100vh;
  overflow: auto;
  box-sizing: border-box;
  padding: 10px;
}
.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin: 15px 0 8px 0;
  padding-left: 5px;
}
.del-btn {
  color: #ff4949;
}
::v-deep .ve-table-expand-tr {
  .ve-table-expand-td-content {
    padding: 0px !important;
    .ve-table-header-th {
      padding: 0px !important;
    }
    .ve-table-body-td {
      padding: 0px !important;
    }
  }
}
.van-cell-group--inset {
  margin: 0;
}
::v-deep .van-cell-group {
  margin-top: 15px;
}
</style>
